/**
 * =============================================================================
 * Institution Users Page ("Používatelia inštitúcií")
 * =============================================================================
 *
 * Management UI for institution-scoped users (doctors and institution admins)
 * stored locally in the admin portal DB (BE: /api/v1/users/).
 *
 * Features:
 * - Institution-scoped user table (email, name, role, institution, status)
 * - Create modal with optional initial password (generator + copy,
 *   password is displayed only once)
 * - Edit name / role (institution editable only for global admins)
 * - Activate / deactivate
 * - Set password modal (generator, password displayed only once)
 * - Invite flow (#14): create without password reports whether the invite
 *   email was sent; per-row "Znovu poslať pozvánku" re-sends the activation
 *   link (the users list exposes no invite/password state, so the action is
 *   offered for every institution-scoped user)
 * - institution_admin: institution is locked to their own institution
 * - #27 (BE #61): server-side debounced search by e-mail / name (the old
 *   client-side search silently ignored the e-mail column), institution
 *   filter, invite status column, honest mail outcome (SMTP disabled/failed
 *   is shown as an error), notification when a user is moved to another
 *   institution
 *
 * Visible to: system_admin, rehapiano_admin, institution_admin.
 */

import { useEffect, useMemo, useState } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import DataTable, { Column } from '../components/DataTable'
import Modal from '../components/Modal'
import { useAuth } from '../auth/AuthProvider'
import {
  Institution,
  InstitutionUser,
  InstitutionUserCreate,
  InstitutionUserLanguage,
  InstitutionUserRole,
  InstitutionUserUpdate,
  institutionUsersApi,
  institutionsApi,
} from '../services/api'
import { getLang } from '../utils/promotionI18n'
import {
  inviteStatusLabel,
  mailProblemMessage,
  ut,
} from '../utils/usersI18n'

/** Inline SK/EN pair for texts that predate the i18n module. */
const bi = (sk: string, en: string): string => (getLang() === 'sk' ? sk : en)

/** Debounce delay of the server-side search input (ms). */
export const SEARCH_DEBOUNCE_MS = 300

const INVITE_BADGE: Record<string, string> = {
  active: 'bg-emerald-100 text-emerald-800',
  pending: 'bg-blue-100 text-blue-800',
  expired: 'bg-amber-100 text-amber-800',
  not_delivered: 'bg-red-100 text-red-800',
  not_invited: 'bg-gray-100 text-gray-700',
}

// =============================================================================
// Constants & helpers
// =============================================================================

const ROLE_OPTIONS: { value: InstitutionUserRole; label: string }[] = [
  { value: 'doctor', label: 'Doktor' },
  { value: 'institution_admin', label: 'Admin inštitúcie' },
]

const ROLE_LABELS: Record<string, string> = {
  doctor: 'Doktor',
  institution_admin: 'Admin inštitúcie',
}

// Invite/e-mail language of the new user (BE #46 — users.language, default sk).
const LANGUAGE_OPTIONS: { value: InstitutionUserLanguage; label: string }[] = [
  { value: 'sk', label: 'Slovenčina' },
  { value: 'en', label: 'English' },
]

/**
 * Roles managed on this page. The BE /users/ list may also contain historical
 * Keycloak-mirrored platform accounts (system_admin/developer/app_admin,
 * typically without an institution) — those are hidden by default (#13).
 */
const INSTITUTION_ROLES: readonly string[] = ['doctor', 'institution_admin']

const PASSWORD_MIN_LENGTH = 8

/**
 * Generate a random password (crypto-strong, no ambiguous characters).
 */
const generatePassword = (length = 14): string => {
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ'
  const lower = 'abcdefghijkmnpqrstuvwxyz'
  const digits = '23456789'
  const special = '!@#$%*-_'
  const all = upper + lower + digits + special

  const randomFrom = (charset: string): string => {
    const values = new Uint32Array(1)
    crypto.getRandomValues(values)
    return charset[values[0] % charset.length]
  }

  // Guarantee at least one char from each class, fill the rest from all.
  const chars = [
    randomFrom(upper),
    randomFrom(lower),
    randomFrom(digits),
    randomFrom(special),
  ]
  while (chars.length < length) {
    chars.push(randomFrom(all))
  }

  // Shuffle (Fisher-Yates with crypto randomness)
  for (let i = chars.length - 1; i > 0; i--) {
    const values = new Uint32Array(1)
    crypto.getRandomValues(values)
    const j = values[0] % (i + 1)
    ;[chars[i], chars[j]] = [chars[j], chars[i]]
  }
  return chars.join('')
}

const formatDateTime = (value?: string | null): string => {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString('sk-SK')
}

/**
 * Translate an API error into a human-readable Slovak message.
 */
const apiErrorMessage = (err: unknown, fallback: string): string => {
  const error = err as {
    response?: { status?: number; data?: { detail?: unknown } }
  }
  if (error?.response?.status === 409) {
    return 'Používateľ s týmto emailom už existuje. Každý email môže byť použitý len raz.'
  }
  const detail = error?.response?.data?.detail
  if (typeof detail === 'string' && detail) {
    return detail
  }
  return fallback
}

const RoleBadge: React.FC<{ role: string }> = ({ role }) => {
  if (role === 'institution_admin') {
    return <span className="badge bg-purple-100 text-purple-800">{ROLE_LABELS[role]}</span>
  }
  if (role === 'doctor') {
    return <span className="badge bg-blue-100 text-blue-800">{ROLE_LABELS[role]}</span>
  }
  return <span className="badge bg-gray-100 text-gray-800">{role}</span>
}

// =============================================================================
// Password field with generator + copy (shared by create & set-password)
// =============================================================================

interface PasswordFieldProps {
  value: string
  onChange: (value: string) => void
  placeholder: string
  testIdPrefix: string
}

const PasswordField: React.FC<PasswordFieldProps> = ({
  value,
  onChange,
  placeholder,
  testIdPrefix,
}) => {
  const [copied, setCopied] = useState(false)

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard API unavailable (e.g. non-secure context) - ignore.
    }
  }

  return (
    <div className="flex gap-2">
      <input
        className="input flex-1 font-mono"
        type="text"
        autoComplete="off"
        placeholder={placeholder}
        value={value}
        data-testid={`${testIdPrefix}-password-input`}
        onChange={(e) => onChange(e.target.value)}
      />
      <button
        type="button"
        className="btn-secondary whitespace-nowrap"
        data-testid={`${testIdPrefix}-generate-password`}
        onClick={() => onChange(generatePassword())}
      >
        Generovať
      </button>
      <button
        type="button"
        className="btn-secondary whitespace-nowrap"
        disabled={!value}
        onClick={copyToClipboard}
      >
        {copied ? 'Skopírované' : 'Kopírovať'}
      </button>
    </div>
  )
}

// =============================================================================
// Page component
// =============================================================================

const InstitutionUsersPage: React.FC = () => {
  const queryClient = useQueryClient()
  const { hasRole } = useAuth()

  // Global admins pick the institution; institution_admin is locked to own.
  const isGlobalAdmin = hasRole('system_admin') || hasRole('rehapiano_admin')
  const isSystemAdmin = hasRole('system_admin')

  // #13: hide historical Keycloak-mirrored platform accounts by default;
  // system_admin can reveal them with the "Zobraziť všetkých" toggle.
  const [showAllUsers, setShowAllUsers] = useState(false)

  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState<InstitutionUser | null>(null)

  // One-time password reveal (shown exactly once after create/set-password).
  const [oneTimePassword, setOneTimePassword] = useState<{
    email: string
    password: string
  } | null>(null)
  const [oneTimeCopied, setOneTimeCopied] = useState(false)

  const emptyCreateForm = {
    email: '',
    first_name: '',
    last_name: '',
    role: 'doctor' as InstitutionUserRole,
    institution_id: '',
    language: 'sk' as InstitutionUserLanguage,
    initialPassword: '',
  }
  const [createForm, setCreateForm] = useState(emptyCreateForm)

  const [editForm, setEditForm] = useState({
    first_name: '',
    last_name: '',
    role: 'doctor' as InstitutionUserRole,
    institution_id: '',
  })

  const [passwordForm, setPasswordForm] = useState({ password: '' })

  // ---------------------------------------------------------------------------
  // Queries
  // ---------------------------------------------------------------------------

  // #27: server-side search (e-mail / name, case-insensitive, partial),
  // debounced so typing does not fire one request per key stroke.
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [institutionFilter, setInstitutionFilter] = useState('')
  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(handle)
  }, [searchInput])

  const { data: usersData, isLoading } = useQuery({
    queryKey: ['institution-users', { search, institutionFilter }],
    queryFn: () =>
      institutionUsersApi.list({
        ...(search ? { search } : {}),
        ...(institutionFilter ? { institution_id: institutionFilter } : {}),
      }),
    placeholderData: keepPreviousData,
  })
  // #13: only institution-scoped roles are shown by default; the full list
  // (incl. platform accounts) is available only to system_admin via toggle.
  const visibleUsers = useMemo(() => {
    const users = usersData?.data || []
    return isSystemAdmin && showAllUsers
      ? users
      : users.filter((user) => INSTITUTION_ROLES.includes(user.role))
  }, [usersData, isSystemAdmin, showAllUsers])

  // Institutions for the select: global admins see all, institution_admin
  // only their own (locked).
  const { data: institutionsData } = useQuery({
    queryKey: ['institutions', 'all'],
    queryFn: () => institutionsApi.list(),
    enabled: isGlobalAdmin,
  })
  const { data: myInstitutionData } = useQuery({
    queryKey: ['institutions', 'me'],
    queryFn: () => institutionsApi.me(),
    enabled: !isGlobalAdmin,
  })

  const institutions: Institution[] = isGlobalAdmin
    ? institutionsData?.data || []
    : myInstitutionData?.data
      ? [myInstitutionData.data]
      : []
  const ownInstitutionId = !isGlobalAdmin ? myInstitutionData?.data?.id || '' : ''

  const refreshUsers = async () => {
    await queryClient.invalidateQueries({ queryKey: ['institution-users'] })
  }

  // ---------------------------------------------------------------------------
  // Mutations
  // ---------------------------------------------------------------------------

  const createMutation = useMutation({
    mutationFn: (payload: InstitutionUserCreate) => institutionUsersApi.create(payload),
    onSuccess: async (response, payload) => {
      setError(null)
      setIsCreateModalOpen(false)
      if (payload.initialPassword) {
        // Show the initial password exactly once - it is never retrievable again.
        setOneTimePassword({
          email: response.data.email,
          password: payload.initialPassword,
        })
        setSuccess(null)
      } else if (response.data.inviteSent) {
        // #14: BE sent an activation email (invite flow, BE #31).
        setSuccess(
          bi(
            `Používateľ ${response.data.email} bol vytvorený. Pozvánka odoslaná na ${response.data.email}.`,
            `User ${response.data.email} was created. Invitation sent to ${response.data.email}.`,
          ),
        )
      } else {
        // Invite was not sent (mail problem is fail-soft on the BE) — the
        // admin can retry via „Znovu poslať pozvánku“ or set a password.
        // #27: show it as an error with the concrete reason when known.
        setSuccess(null)
        setError(
          `${bi(
            `Používateľ ${response.data.email} bol vytvorený, ale pozvánku sa nepodarilo odoslať.`,
            `User ${response.data.email} was created, but the invitation could not be sent.`,
          )} ${
            mailProblemMessage(response.data.email, response.data) ||
            bi(
              'Skúste „Znovu poslať pozvánku“ alebo mu nastavte heslo.',
              'Use “Resend invitation” or set a password.',
            )
          }`,
        )
      }
      setCreateForm(emptyCreateForm)
      await refreshUsers()
    },
    onError: (err) => {
      setSuccess(null)
      setError(apiErrorMessage(err, 'Používateľa sa nepodarilo vytvoriť.'))
    },
  })

  const updateMutation = useMutation({
    mutationFn: (params: { id: string; payload: InstitutionUserUpdate }) =>
      institutionUsersApi.update(params.id, params.payload),
    onSuccess: async (response) => {
      setError(null)
      setSuccess(bi('Zmeny boli uložené.', 'Changes saved.'))
      // #27: institution change -> BE notified the user (or re-invited a
      // not yet activated account); a mail problem must be visible.
      const updated = response.data
      if (updated.notification) {
        const problem = mailProblemMessage(updated.email, updated)
        if (problem) {
          setSuccess(null)
          setError(problem)
        } else {
          setSuccess(
            ut(updated.notification === 'invite' ? 'movedInviteSent' : 'movedNotificationSent', {
              email: updated.email,
              institution: updated.institution_name,
            }),
          )
        }
      }
      setIsEditModalOpen(false)
      setSelectedUser(null)
      await refreshUsers()
    },
    onError: (err) => {
      setSuccess(null)
      setError(apiErrorMessage(err, 'Používateľa sa nepodarilo upraviť.'))
    },
  })

  const statusMutation = useMutation({
    mutationFn: (params: { id: string; active: boolean }) =>
      institutionUsersApi.updateStatus(params.id, {
        status: params.active ? 'active' : 'inactive',
      }),
    onSuccess: async (response) => {
      setError(null)
      setSuccess(
        response.data.is_active
          ? `Používateľ ${response.data.email} bol aktivovaný.`
          : `Používateľ ${response.data.email} bol deaktivovaný.`,
      )
      await refreshUsers()
    },
    onError: (err) => {
      setSuccess(null)
      setError(apiErrorMessage(err, 'Zmena stavu sa nepodarila.'))
    },
  })

  const setPasswordMutation = useMutation({
    mutationFn: (params: { id: string; password: string }) =>
      institutionUsersApi.setPassword(params.id, { password: params.password }),
    onSuccess: async (response, params) => {
      setError(null)
      setSuccess(null)
      setIsPasswordModalOpen(false)
      setSelectedUser(null)
      // Show the new password exactly once.
      setOneTimePassword({ email: response.data.email, password: params.password })
      setPasswordForm({ password: '' })
      await refreshUsers()
    },
    onError: (err) => {
      setSuccess(null)
      setError(apiErrorMessage(err, 'Heslo sa nepodarilo nastaviť.'))
    },
  })

  // #14: re-send the Clinic Portal invitation email (BE invalidates the old
  // invite links and emails a fresh 48h activation link).
  const resendInviteMutation = useMutation({
    mutationFn: (params: { id: string; email: string }) =>
      institutionUsersApi.resendInvite(params.id),
    onSuccess: (response, params) => {
      if (response.data.inviteSent) {
        setError(null)
        setSuccess(ut('resendSent', { email: params.email }))
      } else {
        // Fail-soft on the BE: the email did not go out (#27: say why).
        setSuccess(null)
        setError(
          mailProblemMessage(params.email, response.data) ||
            bi(
              `Pozvánku na ${params.email} sa nepodarilo odoslať. Skúste to znova neskôr.`,
              `The invitation to ${params.email} could not be sent. Try again later.`,
            ),
        )
      }
      void refreshUsers()
    },
    onError: (err) => {
      setSuccess(null)
      setError(apiErrorMessage(err, 'Pozvánku sa nepodarilo odoslať.'))
    },
  })

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const openCreateModal = () => {
    setError(null)
    setSuccess(null)
    setCreateForm({
      ...emptyCreateForm,
      // institution_admin creates users only inside their own institution.
      institution_id: isGlobalAdmin ? '' : ownInstitutionId,
    })
    setIsCreateModalOpen(true)
  }

  const openEditModal = (user: InstitutionUser) => {
    setError(null)
    setSuccess(null)
    setSelectedUser(user)
    setEditForm({
      first_name: user.first_name,
      last_name: user.last_name,
      role: (user.role as InstitutionUserRole) || 'doctor',
      institution_id: user.institution_id || '',
    })
    setIsEditModalOpen(true)
  }

  const openPasswordModal = (user: InstitutionUser) => {
    setError(null)
    setSuccess(null)
    setSelectedUser(user)
    setPasswordForm({ password: '' })
    setIsPasswordModalOpen(true)
  }

  const resendInvite = (user: InstitutionUser) => {
    if (!confirm(ut('resendConfirm', { email: user.email }))) {
      return
    }
    setError(null)
    setSuccess(null)
    resendInviteMutation.mutate({ id: user.id, email: user.email })
  }

  const submitCreate = (e: React.FormEvent) => {
    e.preventDefault()
    setSuccess(null)

    const institutionId = isGlobalAdmin ? createForm.institution_id : ownInstitutionId
    if (!institutionId) {
      setError('Vyberte inštitúciu.')
      return
    }
    if (
      createForm.initialPassword &&
      createForm.initialPassword.length < PASSWORD_MIN_LENGTH
    ) {
      setError(`Heslo musí mať aspoň ${PASSWORD_MIN_LENGTH} znakov.`)
      return
    }

    const payload: InstitutionUserCreate = {
      email: createForm.email.trim(),
      first_name: createForm.first_name.trim(),
      last_name: createForm.last_name.trim(),
      role: createForm.role,
      institution_id: institutionId,
      language: createForm.language,
    }
    if (createForm.initialPassword) {
      payload.initialPassword = createForm.initialPassword
    }
    createMutation.mutate(payload)
  }

  const submitEdit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedUser) return
    setSuccess(null)

    const payload: InstitutionUserUpdate = {
      first_name: editForm.first_name.trim(),
      last_name: editForm.last_name.trim(),
      role: editForm.role,
    }
    if (isGlobalAdmin && editForm.institution_id) {
      payload.institution_id = editForm.institution_id
    }
    updateMutation.mutate({ id: selectedUser.id, payload })
  }

  const submitSetPassword = (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedUser) return
    if (passwordForm.password.length < PASSWORD_MIN_LENGTH) {
      setError(`Heslo musí mať aspoň ${PASSWORD_MIN_LENGTH} znakov.`)
      return
    }
    setPasswordMutation.mutate({ id: selectedUser.id, password: passwordForm.password })
  }

  const copyOneTimePassword = async () => {
    if (!oneTimePassword) return
    try {
      await navigator.clipboard.writeText(oneTimePassword.password)
      setOneTimeCopied(true)
      setTimeout(() => setOneTimeCopied(false), 2000)
    } catch {
      // Clipboard unavailable - user can still select the text manually.
    }
  }

  // ---------------------------------------------------------------------------
  // Table
  // ---------------------------------------------------------------------------

  const columns: Column<InstitutionUser>[] = useMemo(
    () => [
      {
        key: 'email',
        header: 'Email',
        render: (user) => <span className="font-medium">{user.email}</span>,
        searchable: true,
        sortable: true,
        sortFn: (a, b) => a.email.localeCompare(b.email),
      },
      {
        key: 'name',
        header: 'Meno',
        render: (user) => `${user.first_name} ${user.last_name}`.trim() || '—',
        searchable: true,
        searchFn: (user, query) =>
          `${user.first_name} ${user.last_name}`.toLowerCase().includes(query.toLowerCase()),
        sortable: true,
        sortFn: (a, b) => a.last_name.localeCompare(b.last_name),
      },
      {
        key: 'role',
        header: 'Rola',
        render: (user) => <RoleBadge role={user.role} />,
        sortable: true,
        sortFn: (a, b) => a.role.localeCompare(b.role),
      },
      {
        key: 'institution',
        header: 'Inštitúcia',
        render: (user) => user.institution_name || '—',
        searchable: true,
        searchFn: (user, query) =>
          (user.institution_name || '').toLowerCase().includes(query.toLowerCase()),
      },
      {
        key: 'status',
        header: 'Aktívny',
        render: (user) =>
          user.is_active ? (
            <span className="badge badge-active">Aktívny</span>
          ) : (
            <span className="badge badge-inactive">Neaktívny</span>
          ),
      },
      {
        key: 'invite',
        header: ut('inviteColumn'),
        render: (user) => {
          if (!INSTITUTION_ROLES.includes(user.role)) return '—'
          const status = user.invite_status || (user.has_password ? 'active' : 'not_invited')
          const details = [
            user.invite_sent_at ? ut('inviteSentAt', { date: formatDateTime(user.invite_sent_at) }) : '',
            status === 'pending' && user.invite_expires_at
              ? ut('inviteExpiresAt', { date: formatDateTime(user.invite_expires_at) })
              : '',
          ].filter(Boolean)
          return (
            <div data-testid={`invite-status-${user.id}`}>
              <span className={`badge ${INVITE_BADGE[status] || INVITE_BADGE.not_invited}`}>
                {inviteStatusLabel(status)}
              </span>
              {details.length > 0 && (
                <div className="mt-1 text-xs text-gray-500">{details.join(' · ')}</div>
              )}
            </div>
          )
        },
      },
      {
        key: 'last_login',
        header: 'Posledné prihlásenie',
        render: (user) => (
          <span className="text-sm text-gray-600">{formatDateTime(user.last_login_at)}</span>
        ),
      },
      {
        key: 'actions',
        header: 'Akcie',
        render: (user) => (
          <div className="flex flex-wrap gap-2">
            <button
              className="btn-secondary px-2 py-1 text-xs"
              onClick={() => openEditModal(user)}
            >
              Upraviť
            </button>
            <button
              className="btn-secondary px-2 py-1 text-xs"
              onClick={() => openPasswordModal(user)}
            >
              Nastaviť heslo
            </button>
            {INSTITUTION_ROLES.includes(user.role) && user.invite_status !== 'active' && (
              /* #27: hidden once the account is activated (invite_status
                 from BE #61); older BE without the field -> always shown. */
              <button
                className="btn-secondary px-2 py-1 text-xs"
                data-testid={`resend-invite-${user.id}`}
                disabled={resendInviteMutation.isPending}
                onClick={() => resendInvite(user)}
              >
                {ut('resendInvite')}
              </button>
            )}
            <button
              className="btn-secondary px-2 py-1 text-xs"
              data-testid={`toggle-status-${user.id}`}
              disabled={statusMutation.isPending}
              onClick={() => statusMutation.mutate({ id: user.id, active: !user.is_active })}
            >
              {user.is_active ? 'Deaktivovať' : 'Aktivovať'}
            </button>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [statusMutation.isPending, resendInviteMutation.isPending],
  )

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Používatelia inštitúcií</h2>
          <p className="text-sm text-gray-600">
            {bi(
              'Správa lekárov a administrátorov inštitúcií.',
              'Manage doctors and institution administrators.',
            )}
          </p>
          <p className="text-xs text-blue-700">{ut('clinicLoginHint')}</p>
        </div>
        <button className="btn-primary" onClick={openCreateModal} data-testid="open-create-user">
          Pridať používateľa
        </button>
      </div>

      {error && !isCreateModalOpen && !isEditModalOpen && !isPasswordModalOpen && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
          {error}
        </div>
      )}
      {success && (
        <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
          {success}
        </div>
      )}

      {isSystemAdmin && (
        <label className="flex w-fit items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-gray-300"
            data-testid="show-all-users-toggle"
            checked={showAllUsers}
            onChange={(e) => setShowAllUsers(e.target.checked)}
          />
          Zobraziť všetkých (vrátane platformových účtov)
        </label>
      )}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <label className="block flex-1 text-sm text-gray-700">
          {ut('searchLabel')}
          <input
            className="input mt-1"
            type="search"
            data-testid="users-search"
            placeholder={ut('searchPlaceholder')}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </label>
        {isGlobalAdmin && (
          <label className="block text-sm text-gray-700 sm:w-72">
            {ut('filterInstitution')}
            <select
              className="input mt-1"
              data-testid="users-institution-filter"
              value={institutionFilter}
              onChange={(e) => setInstitutionFilter(e.target.value)}
            >
              <option value="">{ut('filterAllInstitutions')}</option>
              {institutions.map((institution) => (
                <option key={institution.id} value={institution.id}>
                  {institution.institution_name}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      <DataTable
        columns={columns}
        data={visibleUsers}
        loading={isLoading}
        getRowKey={(user) => user.id}
        emptyMessage={search ? ut('noUsersFound') : bi('Žiadni používatelia', 'No users')}
      />

      {/* ===================================================================
          Create modal
          =================================================================== */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false)
          setError(null)
        }}
        title="Pridať používateľa inštitúcie"
        size="lg"
      >
        <form className="space-y-3" onSubmit={submitCreate}>
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
              {error}
            </div>
          )}

          <label className="block text-sm text-gray-700">
            Email *
            <input
              className="input mt-1"
              type="email"
              required
              placeholder="doktor@nemocnica.sk"
              value={createForm.email}
              onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
            />
          </label>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="block text-sm text-gray-700">
              Meno *
              <input
                className="input mt-1"
                required
                placeholder="Meno"
                value={createForm.first_name}
                onChange={(e) => setCreateForm({ ...createForm, first_name: e.target.value })}
              />
            </label>
            <label className="block text-sm text-gray-700">
              Priezvisko *
              <input
                className="input mt-1"
                required
                placeholder="Priezvisko"
                value={createForm.last_name}
                onChange={(e) => setCreateForm({ ...createForm, last_name: e.target.value })}
              />
            </label>
          </div>

          <label className="block text-sm text-gray-700">
            Rola *
            <select
              className="input mt-1"
              data-testid="create-role-select"
              value={createForm.role}
              onChange={(e) =>
                setCreateForm({ ...createForm, role: e.target.value as InstitutionUserRole })
              }
            >
              {ROLE_OPTIONS.map((role) => (
                <option key={role.value} value={role.value}>
                  {role.label}
                </option>
              ))}
            </select>
          </label>

          <label className="block text-sm text-gray-700">
            Jazyk komunikácie *
            <select
              className="input mt-1"
              data-testid="create-language-select"
              value={createForm.language}
              onChange={(e) =>
                setCreateForm({
                  ...createForm,
                  language: e.target.value as InstitutionUserLanguage,
                })
              }
            >
              {LANGUAGE_OPTIONS.map((language) => (
                <option key={language.value} value={language.value}>
                  {language.label}
                </option>
              ))}
            </select>
            <span className="mt-1 block text-xs text-gray-500">
              V tomto jazyku dostane používateľ pozvánku a ďalšie e-maily.
            </span>
          </label>

          <label className="block text-sm text-gray-700">
            Inštitúcia *
            <select
              className="input mt-1"
              data-testid="create-institution-select"
              value={isGlobalAdmin ? createForm.institution_id : ownInstitutionId}
              disabled={!isGlobalAdmin}
              required
              onChange={(e) =>
                setCreateForm({ ...createForm, institution_id: e.target.value })
              }
            >
              <option value="">— vyberte inštitúciu —</option>
              {institutions.map((institution) => (
                <option key={institution.id} value={institution.id}>
                  {institution.institution_name}
                </option>
              ))}
            </select>
            {!isGlobalAdmin && (
              <span className="mt-1 block text-xs text-gray-500">
                Používateľov môžete vytvárať len vo vlastnej inštitúcii.
              </span>
            )}
          </label>

          <div className="rounded-lg border border-gray-200 p-3 space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Počiatočné heslo (voliteľné)
            </p>
            <PasswordField
              value={createForm.initialPassword}
              onChange={(value) => setCreateForm({ ...createForm, initialPassword: value })}
              placeholder={`Min. ${PASSWORD_MIN_LENGTH} znakov (nechajte prázdne = bez hesla)`}
              testIdPrefix="create"
            />
            <p className="text-xs text-amber-700">
              Heslo sa zobrazí len raz — po vytvorení používateľa si ho uložte a bezpečne
              odovzdajte. Bez hesla sa používateľ nemôže prihlásiť, kým mu ho nenastavíte.
            </p>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                setIsCreateModalOpen(false)
                setError(null)
              }}
            >
              Zrušiť
            </button>
            <button className="btn-primary" type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Vytvára sa...' : 'Vytvoriť používateľa'}
            </button>
          </div>
        </form>
      </Modal>

      {/* ===================================================================
          Edit modal
          =================================================================== */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false)
          setSelectedUser(null)
          setError(null)
        }}
        title={selectedUser ? `Upraviť: ${selectedUser.email}` : 'Upraviť používateľa'}
        size="lg"
      >
        {!selectedUser ? null : (
          <form className="space-y-3" onSubmit={submitEdit}>
            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
                {error}
              </div>
            )}

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="block text-sm text-gray-700">
                Meno *
                <input
                  className="input mt-1"
                  required
                  value={editForm.first_name}
                  onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                />
              </label>
              <label className="block text-sm text-gray-700">
                Priezvisko *
                <input
                  className="input mt-1"
                  required
                  value={editForm.last_name}
                  onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                />
              </label>
            </div>

            <label className="block text-sm text-gray-700">
              Rola *
              <select
                className="input mt-1"
                data-testid="edit-role-select"
                value={editForm.role}
                onChange={(e) =>
                  setEditForm({ ...editForm, role: e.target.value as InstitutionUserRole })
                }
              >
                {ROLE_OPTIONS.map((role) => (
                  <option key={role.value} value={role.value}>
                    {role.label}
                  </option>
                ))}
              </select>
            </label>

            {isGlobalAdmin && (
              <label className="block text-sm text-gray-700">
                Inštitúcia
                <select
                  className="input mt-1"
                  data-testid="edit-institution-select"
                  value={editForm.institution_id}
                  onChange={(e) =>
                    setEditForm({ ...editForm, institution_id: e.target.value })
                  }
                >
                  {institutions.map((institution) => (
                    <option key={institution.id} value={institution.id}>
                      {institution.institution_name}
                    </option>
                  ))}
                </select>
                <span className="mt-1 block text-xs text-gray-500">{ut('moveHint')}</span>
              </label>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setIsEditModalOpen(false)
                  setSelectedUser(null)
                  setError(null)
                }}
              >
                Zrušiť
              </button>
              <button className="btn-primary" type="submit" disabled={updateMutation.isPending}>
                {updateMutation.isPending ? 'Ukladá sa...' : 'Uložiť zmeny'}
              </button>
            </div>
          </form>
        )}
      </Modal>

      {/* ===================================================================
          Set password modal
          =================================================================== */}
      <Modal
        isOpen={isPasswordModalOpen}
        onClose={() => {
          setIsPasswordModalOpen(false)
          setSelectedUser(null)
          setError(null)
        }}
        title={selectedUser ? `Nastaviť heslo: ${selectedUser.email}` : 'Nastaviť heslo'}
        size="md"
      >
        {!selectedUser ? null : (
          <form className="space-y-3" onSubmit={submitSetPassword}>
            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
                {error}
              </div>
            )}

            <PasswordField
              value={passwordForm.password}
              onChange={(value) => setPasswordForm({ password: value })}
              placeholder={`Nové heslo (min. ${PASSWORD_MIN_LENGTH} znakov)`}
              testIdPrefix="set"
            />
            <p className="text-xs text-amber-700">
              Heslo sa zobrazí len raz — uložte si ho a bezpečne odovzdajte používateľovi.
              Pri prvom prihlásení si ho bude musieť zmeniť.
            </p>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setIsPasswordModalOpen(false)
                  setSelectedUser(null)
                  setError(null)
                }}
              >
                Zrušiť
              </button>
              <button
                className="btn-primary"
                type="submit"
                disabled={setPasswordMutation.isPending}
              >
                {setPasswordMutation.isPending ? 'Nastavuje sa...' : 'Nastaviť heslo'}
              </button>
            </div>
          </form>
        )}
      </Modal>

      {/* ===================================================================
          One-time password reveal modal
          =================================================================== */}
      <Modal
        isOpen={oneTimePassword !== null}
        onClose={() => setOneTimePassword(null)}
        title="Heslo nastavené"
        size="md"
        closeOnBackdrop={false}
      >
        {!oneTimePassword ? null : (
          <div className="space-y-3">
            <p className="text-sm text-gray-700">
              Heslo pre <span className="font-medium">{oneTimePassword.email}</span>:
            </p>
            <div className="flex items-center gap-2">
              <code
                className="flex-1 rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 font-mono text-sm"
                data-testid="one-time-password"
              >
                {oneTimePassword.password}
              </code>
              <button type="button" className="btn-secondary" onClick={copyOneTimePassword}>
                {oneTimeCopied ? 'Skopírované' : 'Kopírovať'}
              </button>
            </div>
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              Toto heslo sa zobrazuje len raz a nedá sa znova zobraziť. Uložte si ho teraz.
            </div>
            <div className="flex justify-end pt-2">
              <button
                type="button"
                className="btn-primary"
                data-testid="close-one-time-password"
                onClick={() => setOneTimePassword(null)}
              >
                Heslo som si uložil
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}

export default InstitutionUsersPage
